import { dist2, idiv, imax, imin, isqrt, withinRange } from '../math/fixed.ts';
import type { BattleState, DeepMark, DeepRules, Enemy, Hero, RootsState } from '../types.ts';
import { castSkill } from '../battle/skills.ts';
import { enemiesNear, findEnemy, targetable } from '../battle/combat.ts';
import { orderMove } from '../battle/heroes.ts';
import { cellAtWorld, heroWalkable } from '../grid.ts';
import { rootsPlay, rootsSolve, type RootNet } from '../battle/druid-layout.ts';
import { deepMarkLive, deepOrder } from '../battle/druid-roots.ts';
import { frostWays, mouthWays } from '../battle/druid-roots-layout.ts';
import { useSkills } from './ghost.ts';

/**
 * The druid's chapter 5 played by the lab's bot (docs/SENDAS.md, «Calibración»; ai/senda-bot.ts sends it here; the net
 * round the chamber is the autumn's `under`, played by its own bot):
 *
 * - the buds below (`mouths`): who knows works out, for each mouth still shut, the knots that give it what it asks
 *   (the nearest way to how they sit now), turns them and waits a pulse; at an open mouth it sows the plan the roots'
 *   rule wins with (the solver); it gets off the drip and strikes what comes at Ceda between. Who doesn't shuts the
 *   side galleries first (stop the leaks), then turns knots one after another; sows every stone at once.
 * - the Carcoma (`carcoma`): who knows cuts each root by its lesson (the mud by the solver's sowing, the resin from
 *   upwind of it, the hidden on a cell where only the rot runs under, the frost by the one way that opens its three
 *   buds), gets off the splinters, the bite and the swipe, strikes her as soon as she takes anything, and shuts
 *   Elvira's knots ahead of her when she goes for the door. Who doesn't sows all at once, lights the patch next to the
 *   root nearest to it, cuts the first black mushroom, shuts the knots of the side galleries, and strikes her.
 */

type Pt = { x: number; y: number };

const go = (s: BattleState, h: Hero, x: number, y: number, slack = 300): void => {
  if (!h.moving && withinRange(h.x, h.y, x, y, 250)) return;
  if (!h.moving || Math.abs(h.destX - x) + Math.abs(h.destY - y) > slack) orderMove(s, h, x, y);
};

const nearestTo = (list: Enemy[], x: number, y: number): Enemy | null => {
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of list) {
    const d = dist2(x, y, e.x, e.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  return best;
};

const strike = (s: BattleState, h: Hero, t: Enemy): void => {
  useSkills(s, h, t);
  if (h.skillCd[2] === 0 && enemiesNear(s, t.x, t.y, 2500).length >= 3) castSkill(s, h, { t: 'skill', slot: 2, x: t.x, y: t.y, id: t.id });
  h.target = t.id;
  h.forced = true;
};

const guard = (s: BattleState, h: Hero, x: number, y: number, r: number, skip = 0): boolean => {
  const t = nearestTo(
    enemiesNear(s, x, y, r, (e) => targetable(s, e) && e.id !== skip),
    x,
    y,
  );
  if (!t) return false;
  strike(s, h, t);
  return true;
};

/** Off a spot to `d` from it: of six ways round the hero, the floor farthest from it (and clear of `bad`). */
const away = (s: BattleState, h: Hero, x: number, y: number, d: number, bad: (px: number, py: number) => boolean = () => false): void => {
  const DX = [1000, 500, -500, -1000, -500, 500];
  const DY = [0, -866, -866, 0, 866, 866];
  let bx = h.x;
  let by = h.y;
  let bd = -1;
  for (let k = 0; k < 6; k++) {
    for (const m of [d, idiv(d * 3, 2), idiv(d * 2, 3)]) {
      const tx = h.x + idiv((DX[k] as number) * m, 1000);
      const ty = h.y + idiv((DY[k] as number) * m, 1000);
      if (!heroWalkable(s, cellAtWorld(s, tx, ty)) || bad(tx, ty)) continue;
      const dd = dist2(tx, ty, x, y);
      if (dd > bd) {
        bd = dd;
        bx = tx;
        by = ty;
      }
      break;
    }
  }
  h.target = 0;
  const w = s.senda?.deep;
  if (w) w.job = -1;
  go(s, h, bx, by, 150);
};

/** The index of a mark of a kind (and net and node) in the rules' list, −1 if none. */
const markOf = (r: DeepRules, k: DeepMark['k'], i: number, j = 0): number => r.marks.findIndex((m) => m.k === k && m.i === i && (k !== 'stone' || m.j === j));

const send = (s: BattleState, h: Hero, at: number): void => {
  const w = s.senda?.deep;
  if (!w || at < 0) return;
  if (w.job === at) return;
  deepOrder(s, h, at);
};

/** Ticks for the hero to walk from where it is to a point. */
const walkTicks = (h: Hero, p: Pt): number => idiv(isqrt(dist2(h.x, h.y, p.x, p.y)) * 12, imax(1, h.stats.speed));

/* ---------- the sowing (a mouth's net, or the mud root's) ---------- */

const SIDE = new WeakMap<BattleState, { cone: number; side: number }>();
const PLAN = new WeakMap<BattleState, Map<number, { key: string; sows: { n: number; t: number }[] }>>();

const plan = (s: BattleState, h: Hero, net: { nodes: Pt[]; edge: number[][] }, i: number, st: RootsState, P: { per: number; see: number; near: number }, at: Pt): { n: number; t: number }[] => {
  const key = `${st.sprout}|${st.knot}|${st.tips.map((t) => `${t.at},${t.mode},${t.to},${t.follow}`).join(';')}`;
  let m = PLAN.get(s);
  if (!m) PLAN.set(s, (m = new Map()));
  const had = m.get(i);
  if (had && had.key === key) return had.sows;
  const N = net as unknown as RootNet;
  // (From a beat ahead: the hero still has to get there and throw.)
  const t0 = s.tick + walkTicks(h, at) + 24;
  const wins = rootsSolve(N, P, 3, 6, { from: st, t0 });
  const H = s.tick + P.per * 40;
  const room = (sows: { n: number; t: number }[]): number => {
    let ok = 0;
    for (let k = 0; k < sows.length; k++) for (const d of [-24, 24, 48]) if (rootsPlay(N, P, sows.map((x, j) => (j === k ? { n: x.n, t: x.t + d } : x)), H, st).won) ok++;
    return idiv(ok * 100, sows.length * 3 + 1);
  };
  const scored = wins.slice(0, 300).map((x) => ({ x, k: room(x.sows) }));
  scored.sort((a, b) => b.k - a.k || a.x.sows.length - b.x.sows.length || a.x.t - b.x.t);
  const sows = scored[0]?.x.sows ?? [];
  m.set(i, { key, sows });
  return sows;
};

/** Sows one net: a tangle cut first; who knows the solver's plan when due; who doesn't every stone at once. True: busy with it. */
const sowNet = (s: BattleState, h: Hero, r: DeepRules, i: number, st: RootsState, knows: boolean): boolean => {
  const w = s.senda?.deep;
  const net = r.nets[i] as { nodes: Pt[]; edge: number[][] };
  if (!w) return false;
  if (st.knot >= 0) {
    send(s, h, markOf(r, 'tangle', i));
    return true;
  }
  if (st.tips.every((t) => t.mode === 3)) return false;
  if (knows) {
    const sows = plan(s, h, net, i, st, r.grow, net.nodes[2] as Pt);
    const due = sows.find((x) => x.t <= s.tick + walkTicks(h, net.nodes[x.n] as Pt) && !(st.sprout & (1 << x.n)));
    if (due) {
      send(s, h, markOf(r, 'stone', i, due.n));
      return true;
    }
    if (sows.some((x) => !(st.sprout & (1 << x.n)))) return false;
    // (The plan sown and still not home, or none: any stone a waiting root sees.)
    if (!st.tips.some((t) => t.mode === 1)) {
      for (let n = 3; n < net.nodes.length; n++) {
        if (st.sprout & (1 << n)) continue;
        if (st.tips.some((t) => t.mode === 0 && (net.edge[t.at] as number[])[n])) {
          send(s, h, markOf(r, 'stone', i, n));
          return true;
        }
      }
    }
    return false;
  }
  for (let n = 3; n < net.nodes.length; n++) {
    if (st.sprout & (1 << n) || st.tips.every((t) => t.trail.includes(n))) continue;
    send(s, h, markOf(r, 'stone', i, n));
    return true;
  }
  return false;
};

/** The knots to turn now toward a set closed: the nearest one that differs (−1: as wanted). */
const turnToward = (closed: number[], want: number[], knots: Pt[], h: Hero): number => {
  let best = -1;
  let bd = 0x7fffffff;
  for (let k = 0; k < closed.length; k++) {
    const should = want.includes(k) ? 1 : 0;
    if ((closed[k] ?? 0) === should) continue;
    const p = knots[k] as Pt;
    const d = dist2(h.x, h.y, p.x, p.y);
    if (d < bd) {
      bd = d;
      best = k;
    }
  }
  return best;
};

/** Of the ways, the one nearest how the knots sit now (fewest turns). */
const nearestWay = (ways: number[][], closed: number[]): number[] | null => {
  let best: number[] | null = null;
  let bc = 99;
  for (const wy of ways) {
    let c = 0;
    for (let k = 0; k < closed.length; k++) if ((wy.includes(k) ? 1 : 0) !== (closed[k] ?? 0)) c++;
    if (c < bc) {
      bc = c;
      best = wy;
    }
  }
  return best;
};

/* ---------- the buds below ---------- */

const mouthsBot = (s: BattleState, h: Hero, r: DeepRules, knows: boolean): void => {
  const M = r.mouths;
  const w = s.senda?.deep;
  if (!M || !w || w.done >= 0 || !w.closed.length) return;
  // Off the drip (who knows).
  if (knows) {
    for (const d of w.drips) {
      if (withinRange(h.x, h.y, d[0] as number, d[1] as number, M.drip.r + 300)) return away(s, h, d[0] as number, d[1] as number, M.drip.r + 700);
    }
  }
  if (w.job >= 0 && w.held > 0) return;
  // What is at Ceda, or on a hero getting low, first; else the work.
  const ceda = s.senda?.npcs.find((n) => n.id === 'ceda');
  if (ceda && guard(s, h, ceda.x, ceda.y, 1100)) return;
  if (h.hp * 100 < h.stats.maxHp * 45 && guard(s, h, h.x, h.y, 1400)) return;
  // An open mouth not clean: its net.
  for (let i = 0; i < M.mouths.length; i++) {
    if ((w.open[i] as number) < 0 || (w.clean[i] as number) >= 0) continue;
    if (sowNet(s, h, r, i, w.roots[i] as RootsState, knows)) return;
  }
  // A mouth still shut: its knots.
  const shut = M.mouths.map((n, i) => ({ n, i })).filter((x) => (w.open[x.i] as number) < 0);
  if (shut.length) {
    const kp = M.knots.map((k) => ({ x: k.x, y: k.y }));
    if (knows) {
      const m = shut[0] as { n: number; i: number };
      const way = nearestWay(mouthWays(M.nodes, M.knots, m.n), w.closed);
      const k = way ? turnToward(w.closed, way, kp, h) : -1;
      if (k >= 0) return send(s, h, markOf(r, 'knot', k));
    } else {
      // Stop the leaks: every side gallery shut; then each knot in turn, a pulse between.
      const drains = M.knots.map((k, i) => ({ k, i })).filter((x) => !(M.nodes[x.k.node] as { need: number }).need && !(M.nodes[x.k.node] as { kids: number[] }).kids.length);
      const open = drains.find((x) => !w.closed[x.i]);
      if (open) return send(s, h, markOf(r, 'knot', open.i));
      if (w.pulses % 3 === 0 && w.t % 36 === 0) return send(s, h, markOf(r, 'knot', idiv(w.pulses, 3) % M.knots.length));
    }
  }
  if (w.job >= 0) return;
  if (ceda && guard(s, h, ceda.x, ceda.y, 3400)) return;
  if (guard(s, h, h.x, h.y, 3600)) return;
  if (ceda) go(s, h, ceda.x + 900, ceda.y + 900);
};

/* ---------- the Carcoma ---------- */

const carcomaBot = (s: BattleState, h: Hero, r: DeepRules, knows: boolean): void => {
  const C = r.carcoma;
  const w = s.senda?.deep;
  if (!C || !w || w.done >= 0 || !w.cut.length) return;
  const boss = findEnemy(s, w.boss);
  if (!boss) return;
  const left = w.cut.filter((x) => !x).length;
  // Off what is coming (who knows): the bite, the swipe, the splinters' cone, fire, the rot from the walls.
  if (knows) {
    if (w.bite >= 0 && withinRange(h.x, h.y, w.bx, w.by, C.bite.r + 400)) return away(s, h, w.bx, w.by, C.bite.r + 900);
    if (w.swipe >= 0 && withinRange(h.x, h.y, w.sx, w.sy, C.swipe.r + 400)) return away(s, h, w.sx, w.sy, C.swipe.r + 900);
    if (w.cone >= 0 && withinRange(h.x, h.y, boss.x, boss.y, C.cone.len + 300)) {
      const ax = w.cx - boss.x;
      const ay = w.cy - boss.y;
      const hx = h.x - boss.x;
      const hy = h.y - boss.y;
      const dot = hx * ax + hy * ay;
      const cross = hx * ay - hy * ax;
      if (dot > 0 && Math.abs(cross) * 10000 <= dot * (C.cone.half + 2500)) {
        // Sideways out of it.
        const d = imax(1, isqrt(ax * ax + ay * ay));
        // (One side for the whole warning: on the line itself the sign would flip every tick.)
        const had = SIDE.get(s);
        const side = had && had.cone === w.cone ? had.side : cross >= 0 ? 1 : -1;
        SIDE.set(s, { cone: w.cone, side });
        const px = h.x + idiv(-ay * 2200 * side, d);
        const py = h.y + idiv(ax * 2200 * side, d);
        w.job = -1;
        h.target = 0;
        return go(s, h, heroWalkable(s, cellAtWorld(s, px, py)) ? px : h.x - idiv(-ay * 2200 * side, d), heroWalkable(s, cellAtWorld(s, px, py)) ? py : h.y - idiv(ax * 2200 * side, d), 150);
      }
    }
    for (let i = 0; i < C.patches.length; i++) {
      const p = C.patches[i] as Pt;
      if ((w.resin[i] as number) > 0 && withinRange(h.x, h.y, p.x, p.y, 750)) return away(s, h, p.x, p.y, 1400);
    }
    if (w.rotR && !withinRange(h.x, h.y, C.mid.x, C.mid.y + 1700, w.rotR - 400)) {
      w.job = -1;
      h.target = 0;
      return go(s, h, C.mid.x, C.mid.y + 1700, 200);
    }
  }
  if (w.job >= 0 && w.held > 0) return;
  // Bare: Elvira's knot ahead of her on her way to the door; between, strike her.
  if (left === 0) {
    // (Only if she would get to the door before a root grows back and pulls her to the middle.)
    const eta = idiv(imax(0, C.door.y - boss.y - C.doorR), imax(1, C.run));
    const back = w.grow >= 0 ? w.growAt - s.tick : 0x7fffffff;
    if (knows && eta < back + 24) {
      for (let g = 0; g < C.gates.length; g++) {
        const G = C.gates[g] as { at: Pt; bar: Pt };
        if (w.gateUsed[g] || w.gate >= 0 || boss.y >= G.bar.y - 300 || boss.y < G.bar.y - 3200) continue;
        return send(s, h, markOf(r, 'gate', g));
      }
    }
    w.job = -1;
    return strike(s, h, boss);
  }
  // What is on the hero, if it is getting low; else the work first.
  if (h.hp * 100 < h.stats.maxHp * 45 && guard(s, h, h.x, h.y, 1100, w.boss)) return;
  // The roots: each by its lesson (who knows), the obvious (who doesn't).
  const order = knows ? [0, 1, 3, 2] : [1, 2, 3, 0];
  for (const k of order) {
    if (w.cut[k]) continue;
    if (k === 0) {
      if (sowNet(s, h, r, 0, w.roots[0] as RootsState, knows)) return;
      continue;
    }
    if (k === 1) {
      if (w.resin.some((x) => x > 0)) continue;
      let pick = -1;
      if (knows) {
        // Upwind of the root, the nearest patch still with resin.
        let bd = 0x7fffffff;
        for (let i = 0; i < C.patches.length; i++) {
          if ((i - C.rootAt) * w.wind >= 0 || w.resin[i] !== 0) continue;
          const d = dist2(h.x, h.y, (C.patches[i] as Pt).x, (C.patches[i] as Pt).y);
          if (d < bd) {
            bd = d;
            pick = i;
          }
        }
      } else {
        // Beside the root, the side nearest the hero.
        let bd = 0x7fffffff;
        for (const i of [C.rootAt - 1, C.rootAt + 1]) {
          if (w.resin[i] !== 0) continue;
          const d = dist2(h.x, h.y, (C.patches[i] as Pt).x, (C.patches[i] as Pt).y);
          if (d < bd) {
            bd = d;
            pick = i;
          }
        }
      }
      const at = pick >= 0 ? markOf(r, 'patch', pick) : -1;
      if (at >= 0 && deepMarkLive(s, at)) return send(s, h, at);
      continue;
    }
    if (k === 2) {
      let pick = -1;
      let bd = 0x7fffffff;
      for (let i = 0; i < C.hidden.cells.length; i++) {
        if (w.hcut[i]) continue;
        const b = C.hidden.black[i] as number;
        const wh = C.hidden.white[i] as number;
        if (!b || (knows && wh)) continue;
        const p = C.hidden.cells[i] as Pt;
        const d = knows ? dist2(h.x, h.y, p.x, p.y) : dist2(boss.x, boss.y, p.x, p.y);
        if (d < bd) {
          bd = d;
          pick = i;
        }
      }
      if (pick >= 0) return send(s, h, markOf(r, 'cell', pick));
      continue;
    }
    // Frost: the one way that opens its three buds (who knows); the side galleries shut (who doesn't).
    const kp = C.frost.knots.map((x) => ({ x: x.x, y: x.y }));
    let want: number[] | null;
    if (knows) want = nearestWay(frostWays(C.frost.nodes, C.frost.knots, C.frost.buds), w.closed);
    else want = C.frost.knots.map((x, i) => ({ x, i })).filter((x) => !(C.frost.nodes[x.x.node] as { need: number }).need && !(C.frost.nodes[x.x.node] as { kids: number[] }).kids.length).map((x) => x.i);
    const t = want ? turnToward(w.closed, want, kp, h) : -1;
    if (t >= 0) return send(s, h, markOf(r, 'knot', t));
  }
  if (w.job >= 0) return;
  // Nothing to cut for now: strike her if anything lands, else what is on the colliers, else stand by her.
  if (left < 4) return strike(s, h, boss);
  if (guard(s, h, C.door.x, C.door.y, 2600, w.boss)) return;
  h.target = 0;
  go(s, h, C.mid.x, C.mid.y + 3200);
};

export const deepBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const r = s.rules?.senda?.deep;
  if (!r) return;
  if (r.mouths) return mouthsBot(s, h, r, knows);
  if (r.carcoma) return carcomaBot(s, h, r, knows);
  void imin;
};
